import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage3() {
      const links = {
    prev: "/islamic-trade-page-22", 
    index: "/index",                 
    next: "/islamic-trade-page-24", 
  };
 const contentRef = useRef(null);
  const titleText = "Task-Specific Reward Contract (Ja'aalah)";
  const questions = [
    {
      text: "1. 1. Which of the following best describes the concept of Ja’aalah?",
      options: [
        {
          value: "A",
          label:
            "A. A. A fixed employment contract where both work and payment are predetermined.",
        },
        {
          value: "B",
          label:
            "B. A partnership agreement where both parties share profits and losses.",
        },
        {
          value: "C",
          label:
            "C. A contract offering a specified reward for completing a task with uncertain effort or time.",
        },
        {
          value: "D",
          label:
            "D. A loan contract with deferred payment and guaranteed return.",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Which of the following Hadith supports the permissibility of Ja’aalah?",
      options: [
        { value: "A", label: "A. “The buyer and the seller have the right to annul the sale before they part.”" },
        { value: "B", label: "B. The incident where Abu Saeed Al-Khudri recited the Qur’an to heal a man and accepted a flock of sheep as reward" },
        { value: "C", label: "C. “Whoever cheats us is not one of us.”" },
        { value: "D", label: "D. “The best of people are those who are most beneficial to others.”" },
      ],
      correct: "B",
    },
    {
      text: "3. What happens if the person offering a Ja’aalah contract annuls it after the worker has suffered a loss?",
      options: [
        { value: "A", label: "A. The worker has no right to compensation under any circumstance" },
        { value: "B", label: "B. The worker is entitled to fair compensation for the harm caused" },
        { value: "C", label: "C. The contract automatically becomes void and unenforceable" },
        { value: "D", label: "D. The worker must complete the task without any reward" },
      ],
      correct: "B",
    },
    {
      text: "4. According to Islamic principles, in a dispute over the agreed reward in Ja’aalah, who bears the burden of proof?",
      options: [
        { value: "A", label: "A. The person who denies the claim" },
        { value: "B", label: "B. The person who makes the claim" },
        { value: "C", label: "C. Both parties equally" },
        { value: "D", label: "D. The arbitrator or judge" },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
    <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={23}
        timerMinutes={60}
        currentLevel={1}
      />
     

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 23</b>! Today, we delve into the dynamic and flexible
        Islamic contract known as Ja’aalah, a task-based reward agreement.
        Rooted in the principles of fairness, transparency, and mutual benefit,
        Ja’aalah provides a practical solution for situations where the exact
        time or effort required cannot be predetermined, such as finding a lost
        item or solving a complex problem. Through this lesson, we’ll understand
        how Ja’aalah differs from fixed employment contracts, how it is
        supported by prophetic traditions, and how it ensures justice for both
        parties while accommodating real-life challenges. Ja’aalah is an Islamic
        contract in which a specified reward or compensation is offered for
        completing a task where the exact amount of work or the time required is
        unknown. This type of agreement is particularly useful in scenarios
        where predefined employment terms are impractical. It aligns with the
        principles of fairness and flexibility in Islamic finance and contract
        law. <br />
        For example, a person might announce, “Whoever finds my lost cow will be
        rewarded with 1 BTC.” In this scenario, the compensation is
        predetermined, but the effort or duration required to accomplish the
        task remains uncertain. This type of contract is permissible in Islam
        and serves as a practical solution for tasks where precise parameters
        cannot be defined in advance. It provides flexibility in achieving
        necessary objectives while maintaining a clear understanding of the
        reward. <br />
        This concept is distinct from Ijaarah, the standard contract for wages,
        where both the scope of work and compensation must be clearly agreed
        upon at the outset. In Ja’aalah, the absence of specific details about
        the workload is excused due to the nature of the agreement. This type of
        contract is especially permitted in cases of necessity or public
        benefit, where rigid conditions might hinder the completion of vital
        tasks. For example, someone could offer a reward for finding a missing
        item or solving a problem that benefits the community.
        <br /> The permissibility of Ja’aalah is supported by a prophetic
        tradition involving Abu Saeed Al-Khudri (may Allah be pleased with him). In this incident, Abu Saeed
        recited verses of the Quran to heal a man who had been stung by a
        scorpion. Upon successfully curing him, Abu Saeed accepted a flock of
        sheep as a reward. When this was brought to the attention of the Prophet
        Muhammad (peace be upon him), he approved the action, thereby affirming
        its legitimacy.{" "}
        <i>
          <a href="https://sunnah.com/muslim:2201a" target="blank">
            (Sahih Al Bukhari 2201)
          </a>
        </i>
        <br /> This incident underscores the flexibility and practicality of
        Ja’aalah as a lawful form of agreement in Islam. By allowing Ja’aalah,
        Islamic law demonstrates its adaptability in addressing unique
        situations while ensuring fairness and clarity. It provides a way to
        balance necessity with ethical considerations, highlighting the dynamic
        nature of Islamic jurisprudence in facilitating human interactions and
        problem-solving.
        <br />
        <br /> <b>Fulfillment of Promised Reward</b>
        <br />
        <br /> If a person offers a significant reward for completing a task and
        it turns out to be easier than anticipated, the promised reward cannot
        be reduced or renegotiated. For example, if someone announces, “I will
        give 1 BTC to whoever finds my lost cow,” assuming it would be difficult
        to locate, and the cow is found quickly and with little effort, the
        finder is still fully entitled to the entire 1 BTC reward without any
        deductions. This is because the contract of Ja’aalah is based on mutual
        consent and clarity of the reward at the time of the agreement. The ease
        or difficulty of completing the task does not alter the binding nature
        of the contract once it has been established.
        <br /> This principle is rooted in the Islamic emphasis on upholding
        agreements and avoiding injustice. Altering the terms after the task has
        been completed would violate the fairness and trust inherent in such
        contracts. The Quran emphasizes the importance of fulfilling promises in
        several verses, such as: “O believers! Honour your obligations. ”
        <i>
          <a href="https://quran.com/5/1" target="blank">
            (Surah Al-Ma'idah 5:1)
          </a>
        </i>
        <br />
        Additionally, the Hadith, “Muslims are bound by their conditions”
        <i>
          <a href="https://sunnah.com/abudawud:3594" target="blank">
            (Sunan Abu Dawood, Hadith 3594)
          </a>
        </i>{" "}
        <br /> The above hadith further reinforces that both parties must adhere
        to the agreed terms, ensuring that the finder receives their full and
        rightful reward, regardless of the circumstances under which the task
        was completed. This demonstrates the justice and practicality embedded
        within Islamic contractual principles.
        <br />
        <br />
        <b>Clarity of Reward in Ja’aalah </b>
        <br />
        <br />
        In a Ja’aalah contract, the reward must be explicitly stated and known
        to all parties involved. It is impermissible to make ambiguous offers
        such as, “Whoever finds my lost cow will get whatever is inside this
        bag,” without disclosing the contents of the bag. The bag might contain
        something valuable, such as gold, or it might be completely empty,
        leading to uncertainty and potential disputes. Islamic law prohibits
        such ambiguity to ensure fairness and transparency in agreements.
        <br /> The Quran emphasizes the importance of clear dealings, as in the
        verse: “And do not consume one another’s wealth unjustly”{" "}
        <i>
          <a href="https://quran.com/4/29" target="blank">
            (Surah An-Nisa 4:29)
          </a>
        </i>
        <br />
        By explicitly defining the reward in advance, the contract upholds trust
        and avoids any possibility of deception or dissatisfaction between the
        parties. <br />
        <br />
        <br />
        <b>Reward Through a Share of the Item</b> <br />
        <br />
        In a Ja’aalah contract, it is permissible to reward the finder of a lost
        item with a share of the item itself. For example, a person may
        announce, “Whoever finds my lost cow will receive half of its meat.”
        This is a valid form of compensation as long as the reward is clearly
        specified. The principle behind this is that the reward is tied to the
        successful completion of the task and is agreed upon at the time of the
        contract, ensuring fairness and mutual understanding. <br />
        This principle extends to non-material rewards as well, such as shared
        profits or benefits derived from an achievement. If someone offers a
        percentage of a discovered treasure or a portion of revenue from a
        successfully completed research project, this falls within the bounds of
        Ja’aalah as long as the division is clear and agreed upon at the outset.
        This flexibility allows the contract to adapt to different scenarios
        while maintaining its core principles of transparency and fairness.
        <br />
        <br />{" "}
        <b>Reward for Procuring or Producing Items Based on Their Attributes</b>
        <br />
        <br />
        It is also permissible in Islamic law to offer a reward for producing or
        procuring an item based on a clear description of its attributes. For
        instance, one may say to a tailor, “I will give you a reward if you
        stitch a garment for me with such-and-such specifications.” Similarly,
        it is valid to say, “Whoever builds a wall for me of such-and-such
        height and width will receive a specific reward.” <br />
        As long as the description of the task is detailed and unambiguous, and
        the reward is explicitly stated, the agreement is valid and enforceable.
        These examples reflect the flexibility of Ja’aalah in facilitating
        agreements for tasks where the reward incentivizes completion, ensuring
        both fairness and clarity for all parties involved. This aligns with
        Islamic principles of transparency and justice, creating a framework
        where both the task and its compensation are mutually understood. <br />
        <br />
        <b>
          Permissibility to Annul and Compensation in Case of Harm Due to
          Annulment{" "}
        </b>
        <br />
        <br />
        In a Ja’aalah contract, it is permissible for either party to annul the
        agreement at any time without any obligation to pay compensation,
        provided no harm is caused by the annulment. However, if the one
        offering the reward terminates the contract in a way that causes harm to
        the other party, they are required to provide fair compensation. For
        example, if a person announces, “Whoever builds a wall of such-and-such
        type for me will receive a specific amount,” and a mason, hearing this,
        leaves other jobs and begins building the wall, the situation changes.
        If the offeror then annuls the contract after half the wall is built,
        the mason, having suffered monetary loss and disruption to his work,
        must be compensated for his effort and losses.
        <br /> This principle ensures that the rights of all parties are
        preserved and that no injustice arises from a sudden or arbitrary
        annulment of a contract. It reflects the balance in Islamic
        jurisprudence between flexibility in agreements and the responsibility
        to prevent undue harm to others. Additionally, it emphasizes the ethical
        responsibility of fulfilling one’s word in good faith, reinforcing
        Islamic principles of fairness and accountability in all contractual
        dealings.
        <br />
        <br />
        <b>Dispute Resolution in Ja’aalah Contracts</b>
        <br />
        <br />
        In cases of dispute over the agreed reward in a Ja’aalah contract,
        Islamic principles dictate that the claim of the person who announced
        the reward will generally be given preference unless proven otherwise.
        This is based on the legal maxim that financial obligations cannot be
        imposed without clear evidence. For example, if a mason claims he was
        promised 1 BTC for building a wall but has no proof, and the person who
        commissioned the wall asserts that the agreed reward was only 0.5 BTC,
        then the latter's claim is upheld. This ruling aligns with the Islamic
        principle that the burden of proof lies upon the claimant, and an oath
        is taken by the one who denies the claim.
        <br />
        The Prophet Muhammad (peace be upon him) said: "If people were given
        everything that they claimed, men would claim the wealth and lives of
        others. But the burden of proof is upon the claimant, and the oath is
        upon the one who denies." (Sunan al-Bayhaqi, Hadith 21317)
        <br />
        However, an exception exists when the stated reward appears unreasonably
        low, and the mason’s claim aligns with the fair market price for such
        work. In such a case, the judge or arbitrator may rule in favor of the
        mason, using customary pricing as indirect evidence to establish
        fairness. This approach ensures that Ja’aalah contracts maintain their
        integrity while preventing unjust enrichment or exploitation.
        <br />
        <br />
        <b>Summary Of Today's Lesson</b> <br />
        <br />
        Today’s lesson focused on Ja’aalah, an Islamic contract that allows a
        pre-agreed reward to be offered for completing a task where the time or
        effort required is uncertain such as finding a lost item or solving a
        problem. Unlike standard employment contracts (Ijaarah), Ja’aalah offers
        flexibility while upholding the principles of fairness, transparency,
        and mutual consent. We explored how this contract is rooted in prophetic
        tradition and is permissible under Islamic law even when the scope of
        the work is undefined, provided the reward is clearly stated. We learned
        that once a reward is offered and the task completed, the full payment
        is due regardless of the effort involved. The lesson also clarified that
        rewards can be in the form of a share of the recovered item or a
        specified portion of a produced good, so long as the terms are
        transparent. While either party may annul the contract at any time,
        compensation is due if harm is caused by the termination. In case of
        disputes, the burden of proof lies with the claimant, although
        exceptions may be made when market norms support a fairer outcome.
        Overall, Ja’aalah demonstrates the adaptability of Islamic jurisprudence
        in balancing ethical conduct, practical needs, and contractual justice.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br />
        In our next lesson, we shift from task-based contracts to the timeless
        institution of Waqf i.e Islamic endowment. We’ll explore how this
        powerful form of ongoing charity (Sadaqah Jariyah) allows Muslims to
        dedicate assets for the perpetual benefit of others, from land and
        buildings to even money and consumable goods. You'll learn about the
        foundational principles behind Waqf, its legal conditions, the
        importance of clarity in defining the asset, and the rulings on
        endowments made to oneself, to others, or for public benefit. This
        lesson highlights the flexibility, structure, and social impact of Waqf
        in Islamic jurisprudence ensuring that charitable contributions continue
        to serve future generations with fairness, integrity, and lasting
        reward.
      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
              <Question
                key={index}
                questionText={q.text}
                options={q.options}
                correctAnswer={q.correct}
                onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
              />
            ))}
      
            <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
              Score: {score} / {questions.length}
            </div>
      
  
      <FloatingPageNav 
                   prevLink={links.prev} 
                   indexLink={links.index} 
                   nextLink={links.next} 
                 />
    </div>
  );
}

export default IslamicTradePage3;
